import type React from 'react'
import { useEffect, useState } from 'react'
import type qrcode from 'qrcode-generator'
import { useNls } from '../../nls/nlsProvider'
import { waMeInviteLink } from '../../utils/whatsappLinks'
import './learnerSlips.css'

export type JoinCodeSlipItem = {
  key: string
  displayName: string
  subtitle?: string
  code: string
  pin: string
  expiresAtMs: number
}

export type JoinCodeSlipsProps = {
  slips: JoinCodeSlipItem[]
  workspaceName: string
  whatsappNumberE164?: string | null
}

const SLIPS_PER_PAGE = 8

export const JoinCodeSlips: React.FC<JoinCodeSlipsProps> = ({
  slips,
  workspaceName,
  whatsappNumberE164,
}) => {
  const { t } = useNls()
  const [qrSvgs, setQrSvgs] = useState<Record<string, string>>({})
  const [whatsappQrSvg, setWhatsappQrSvg] = useState<string>('')

  const whatsappInviteUrl = whatsappNumberE164 ? waMeInviteLink(whatsappNumberE164) : null

  useEffect(() => {
    let isMounted = true
    if (whatsappInviteUrl) {
      import('qrcode-generator')
        .then((mod) => {
          if (!isMounted) return
          const qrcodeFactory =
            (mod as { default?: typeof qrcode }).default || (mod as unknown as typeof qrcode)
          try {
            const qr = qrcodeFactory(0, 'M')
            qr.addData(whatsappInviteUrl)
            qr.make()
            setWhatsappQrSvg(qr.createSvgTag({ scalable: true }))
          } catch (err) {
            console.warn('[JoinCodeSlips] failed to generate WhatsApp QR', err)
          }
        })
        .catch((err) => {
          console.warn('[JoinCodeSlips] failed to load qrcode-generator for WhatsApp', err)
        })
    }
    return () => {
      isMounted = false
      setWhatsappQrSvg('')
    }
  }, [whatsappInviteUrl])

  useEffect(() => {
    let isMounted = true
    import('qrcode-generator')
      .then((mod) => {
        if (!isMounted) return
        const qrcodeFactory =
          (mod as { default?: typeof qrcode }).default || (mod as unknown as typeof qrcode)
        const map: Record<string, string> = {}
        for (let i = 0; i < slips.length; i++) {
          const slip = slips[i]
          try {
            const origin = typeof window !== 'undefined' ? window.location.origin : ''
            const url = `${origin}/join?code=${encodeURIComponent(slip.code)}`
            const qr = qrcodeFactory(0, 'M')
            qr.addData(url)
            qr.make()
            map[slip.code] = qr.createSvgTag({ scalable: true })
          } catch (err) {
            console.warn('[JoinCodeSlips] failed to generate QR code for slip at index', i, err)
          }
        }
        setQrSvgs(map)
      })
      .catch((err) => {
        console.warn('[JoinCodeSlips] failed to load qrcode-generator', err)
      })

    return () => {
      isMounted = false
    }
  }, [slips])

  const pages: JoinCodeSlipItem[][] = []
  for (let i = 0; i < slips.length; i += SLIPS_PER_PAGE) {
    pages.push(slips.slice(i, i + SLIPS_PER_PAGE))
  }

  return (
    <div className="learnerSlips" data-testid="join-code-slips">
      {pages.map((pageSlips, pageIdx) => (
        <div key={pageIdx} className="learnerSlips__page" data-testid="learner-slips-page">
          {pageSlips.map((slip) => {
            const dateStr = new Date(slip.expiresAtMs).toLocaleDateString()
            return (
              <div
                key={slip.key}
                className="learnerSlips__slip"
                data-testid="learner-slip-card"
              >
                <div className="learnerSlips__header">
                  <span className="learnerSlips__workspace">{workspaceName}</span>
                  <div className="learnerSlips__nameBlock">
                    <span className="learnerSlips__name">{slip.displayName}</span>
                    {slip.subtitle && (
                      <span className="learnerSlips__subtitle">{slip.subtitle}</span>
                    )}
                  </div>
                </div>

                <div className="learnerSlips__main">
                  <div className="learnerSlips__credentials">
                    <div className="learnerSlips__credRow">
                      <span className="learnerSlips__credLabel">
                        {t('NLSJoinCodesSlipCode')}:
                      </span>
                      <span className="learnerSlips__code">{slip.code}</span>
                    </div>
                    <div className="learnerSlips__credRow">
                      <span className="learnerSlips__credLabel">
                        {t('NLSJoinCodesSlipPin')}:
                      </span>
                      <span className="learnerSlips__pin">{slip.pin}</span>
                    </div>
                  </div>

                  <div className="learnerSlips__qrContainer">
                    {qrSvgs[slip.code] ? (
                      <div
                        className="learnerSlips__qrSvg"
                        dangerouslySetInnerHTML={{ __html: qrSvgs[slip.code] }}
                      />
                    ) : (
                      <div className="learnerSlips__qrPlaceholder" />
                    )}
                  </div>

                  {whatsappNumberE164 && (
                    <div className="learnerSlips__whatsappBlock" data-testid="learner-slip-whatsapp-qr">
                      <div className="learnerSlips__whatsappQrContainer">
                        {whatsappQrSvg ? (
                          <div
                            className="learnerSlips__whatsappQrSvg"
                            dangerouslySetInnerHTML={{ __html: whatsappQrSvg }}
                          />
                        ) : (
                          <div className="learnerSlips__qrPlaceholder" />
                        )}
                      </div>
                      <span className="learnerSlips__whatsappCaption">
                        {t('NLSWhatsappSlipQrCaption')}
                      </span>
                    </div>
                  )}
                </div>

                <div className="learnerSlips__instructions">
                  <ol className="learnerSlips__steps">
                    <li>{t('NLSJoinCodesSlipStep1')}</li>
                    <li>{t('NLSJoinCodesSlipStep2')}</li>
                    <li>{t('NLSJoinCodesSlipStep3')}</li>
                  </ol>
                </div>

                <div className="learnerSlips__footer">
                  <span className="learnerSlips__expires">
                    {t('NLSJoinCodesExpiresAt', { date: dateStr })}
                  </span>
                  <span className="learnerSlips__privacy">
                    {t('NLSJoinCodesSlipKeepPrivate')}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}
